// Theme: light / dark / system. Persists the user's choice in
// localStorage and applies it via a data-theme attribute on <html>.
//
// Storage values: 'light' | 'dark' | 'system' | null
//   'light' / 'dark' → force that palette regardless of OS
//   'system'         → follow prefers-color-scheme via a MediaQueryList listener
//   null (unset)     → same as 'system' on first load
//
// The CSS in globals.css branches on the data-theme attribute first, and
// falls back to the prefers-color-scheme media query when the attribute
// is absent — so the very first paint (before this module's effect
// runs) honors the OS even though we haven't applied data-theme yet.

import { useEffect, useState } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'haket.theme';

function readStored(): ThemeChoice {
  if (typeof localStorage === 'undefined') return 'system';
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'system';
}

// Same-tab change signal. storage events only fire in OTHER tabs, so when we
// write the theme programmatically (e.g. hydrating from the server on login, or
// the toggle) we also dispatch this so a mounted useTheme in THIS tab re-reads.
const THEME_EVENT = 'haket.theme-changed';

/**
 * Imperatively set the stored theme (localStorage) and notify listeners in this
 * tab. Used to hydrate the local fast-path from the server's saved value on
 * login WITHOUT writing back to the server (that would be a redundant echo).
 * No-ops if the value already matches, so hydration can't loop.
 */
export function applyStoredTheme(choice: ThemeChoice): void {
  if (typeof localStorage === 'undefined') return;
  if (localStorage.getItem(STORAGE_KEY) === choice) return;
  try { localStorage.setItem(STORAGE_KEY, choice); } catch { /* private mode */ }
  window.dispatchEvent(new Event(THEME_EVENT));
}

/** The current stored theme choice (defaults to 'system' when unset). */
export function getStoredTheme(): ThemeChoice {
  return readStored();
}

function applyAttribute(resolved: ResolvedTheme | null): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (resolved === null) {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', resolved);
  }
}

function osPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}


/** Resolve a stored choice to the concrete palette to paint. */
function resolveChoice(choice: ThemeChoice): ResolvedTheme {
  if (choice === 'light' || choice === 'dark') return choice;
  return osPrefersDark() ? 'dark' : 'light';
}

let themeInitialised = false;

/**
 * Apply the stored theme to <html data-theme> and keep it applied — APP-WIDE,
 * independent of any React component. Call this ONCE at app entry (main.tsx):
 * previously only useTheme() (mounted solely in <ThemeToggle>) ever set
 * data-theme, so a refresh on any page WITHOUT the toggle left the attribute
 * unset and the CSS fell back to the OS default (dark) — the "theme only works
 * on Preferences" bug. This makes light/dark/system all stick on every route.
 *
 * Idempotent (guards against double-init). Keeps the attribute current on:
 *  - same-tab changes (THEME_EVENT, e.g. the toggle or login seed),
 *  - other-tab changes (storage event),
 *  - OS scheme flips while the choice is 'system' (matchMedia).
 */
export function initTheme(): void {
  if (themeInitialised || typeof window === 'undefined') return;
  themeInitialised = true;

  const apply = () => applyAttribute(resolveChoice(readStored()));
  apply(); // immediate first paint

  // Re-apply on any stored-value change (this tab or another).
  window.addEventListener(THEME_EVENT, apply);
  window.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) apply(); });
  // Re-apply when the OS scheme changes (only affects the 'system' choice, but
  // re-resolving for an explicit light/dark is a harmless no-op).
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', apply);
}

/** Reactive accessor. Returns the current choice + the resolved palette
 *  + a setter that persists + re-applies. */
export function useTheme(): {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (c: ThemeChoice) => void;
} {
  const [choice, setChoiceState] = useState<ThemeChoice>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    choice === 'system' ? (osPrefersDark() ? 'dark' : 'light') : choice
  );

  // Track the resolved palette for the toggle's label + apply it. Applying here
  // is idempotent with initTheme() (both set the SAME resolved data-theme), so
  // they never fight; initTheme is what guarantees it's applied on pages where
  // this hook isn't mounted. We always set an EXPLICIT data-theme (even for
  // 'system' → resolved light/dark) rather than removing the attribute, so the
  // app-wide initTheme and this hook stay consistent.
  useEffect(() => {
    if (choice === 'light' || choice === 'dark') {
      applyAttribute(choice);
      setResolved(choice);
      return;
    }
    // 'system': resolve against the OS preference and follow changes.
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => {
      const r = mql.matches ? 'dark' : 'light';
      applyAttribute(r);
      setResolved(r);
    };
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, [choice]);

  // Re-read when the stored value changes out-of-band: another tab (storage
  // event) or a programmatic applyStoredTheme in THIS tab (custom event, e.g.
  // server hydration on login). Keeps the toggle + applied palette in sync.
  useEffect(() => {
    const reread = () => setChoiceState(readStored());
    const onStorage = (e: StorageEvent) => { if (e.key === STORAGE_KEY) reread(); };
    window.addEventListener(THEME_EVENT, reread);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(THEME_EVENT, reread);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setChoice = (c: ThemeChoice) => {
    // Persist + notify (applyStoredTheme dispatches THEME_EVENT). Also set state
    // directly so the change is immediate even though the event is synchronous.
    applyStoredTheme(c);
    setChoiceState(c);
  };

  return { choice, resolved, setChoice };
}
